import { useState, useMemo, memo, useCallback } from 'react'
import { useSession } from '../context'
import { formatRelativeTime, formatDateTime, truncate } from '../utils'

export default memo(function Sidebar({ isOpen, isVisible = true, onClose, searchInputRef }) {
  const { sessions, currentSessionId, selectSession, deleteSession, deleteSessions } = useSession()
  const [searchTerm, setSearchTerm] = useState('')
  const [deletingSessionId, setDeletingSessionId] = useState(null)
  const [deleteMessage, setDeleteMessage] = useState('')
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set())
  const [isBatchDeleting, setIsBatchDeleting] = useState(false)

  const filteredSessions = useMemo(() => {
    if (!searchTerm) return sessions
    const term = searchTerm.toLowerCase()
    return sessions.filter(session =>
      session.session_id.toLowerCase().includes(term)
    )
  }, [sessions, searchTerm])

  const sortedSessions = useMemo(() => {
    return [...filteredSessions].sort((a, b) =>
      new Date(b.lastActivity) - new Date(a.lastActivity)
    )
  }, [filteredSessions])

  const handleSessionClick = useCallback((sessionId) => {
    if (isSelectMode) {
      setSelectedSessionIds(prev => {
        const next = new Set(prev)
        if (next.has(sessionId)) {
          next.delete(sessionId)
        } else {
          next.add(sessionId)
        }
        return next
      })
      return
    }
    selectSession(sessionId)
    onClose?.()
  }, [isSelectMode, selectSession, onClose])

  const handleToggleSelect = useCallback((sessionId, e) => {
    e?.stopPropagation?.()
    setSelectedSessionIds(prev => {
      const next = new Set(prev)
      if (next.has(sessionId)) {
        next.delete(sessionId)
      } else {
        next.add(sessionId)
      }
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    if (selectedSessionIds.size === sortedSessions.length) {
      setSelectedSessionIds(new Set())
    } else {
      setSelectedSessionIds(new Set(sortedSessions.map(s => s.session_id)))
    }
  }, [selectedSessionIds, sortedSessions])

  const handleDelete = useCallback(async (sessionId) => {
    if (!sessionId || deletingSessionId) return
    setDeletingSessionId(sessionId)
    setDeleteMessage('Deleting session...')
    try {
      await deleteSession(sessionId)
      setDeleteMessage('Session deleted!')
      setTimeout(() => {
        setDeletingSessionId(null)
        setDeleteMessage('')
      }, 800)
    } catch {
      setDeleteMessage('Failed to delete')
      setTimeout(() => {
        setDeletingSessionId(null)
        setDeleteMessage('')
      }, 1500)
    }
  }, [deleteSession, deletingSessionId])

  const handleBatchDelete = useCallback(async () => {
    if (selectedSessionIds.size === 0 || isBatchDeleting) return
    const idsToDelete = Array.from(selectedSessionIds)
    setDeletingSessionId('batch')
    setIsBatchDeleting(true)
    setDeleteMessage(`Deleting ${idsToDelete.length} session${idsToDelete.length > 1 ? 's' : ''}...`)

    try {
      await deleteSessions(idsToDelete)
      setDeleteMessage('Sessions deleted!')
      setSelectedSessionIds(new Set())
      setIsSelectMode(false)
      setTimeout(() => {
        setDeletingSessionId(null)
        setIsBatchDeleting(false)
        setDeleteMessage('')
      }, 800)
    } catch {
      setDeleteMessage('Batch delete failed')
      setTimeout(() => {
        setDeletingSessionId(null)
        setIsBatchDeleting(false)
        setDeleteMessage('')
      }, 1500)
    }
  }, [selectedSessionIds, isBatchDeleting, deleteSessions])

  const shouldShow = isOpen || (typeof window !== 'undefined' && window.innerWidth >= 1024 && isVisible)

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={onClose}
        />
      )}
      
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-[290px] max-w-[85vw] flex flex-col
          transform-gpu transition-all duration-200 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isVisible ? 'lg:translate-x-0 lg:w-[290px]' : 'lg:-translate-x-full lg:w-0 lg:opacity-0'}
        `}
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRight: '1px solid var(--color-border)'
        }}
      >
        {/* Header */}
        <div className="p-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-1.5">
              <h2 className="text-xs font-bold uppercase tracking-wider flex items-center" style={{ color: 'var(--text-primary)' }}>
                Sessions
                <span 
                  className="ml-1.5 text-xs font-normal px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}
                >
                  {sessions.length}
                </span>
              </h2>

              {/* Multi-Select Toggle Icon Button */}
              <button
                onClick={() => {
                  setIsSelectMode(prev => !prev)
                  setSelectedSessionIds(new Set())
                }}
                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all ml-1 ${
                  isSelectMode ? 'bg-[var(--color-accent)] text-white shadow-sm' : 'text-[var(--text-muted)] hover:bg-[var(--color-hover)]'
                }`}
                title={isSelectMode ? 'Exit Selection Mode' : 'Select Multiple Sessions'}
                aria-label="Toggle multi-select"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-1">
              {/* Batch Actions when in Select Mode */}
              {isSelectMode && (
                <>
                  <button
                    onClick={handleToggleSelectAll}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[var(--text-muted)] hover:bg-[var(--color-hover)] transition-colors"
                    title={selectedSessionIds.size === sortedSessions.length ? 'Deselect All' : 'Select All'}
                    aria-label="Toggle select all"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      {selectedSessionIds.size === sortedSessions.length && <polyline points="9 11 12 14 22 4"></polyline>}
                    </svg>
                  </button>

                  <button
                    onClick={handleBatchDelete}
                    disabled={selectedSessionIds.size === 0 || isBatchDeleting}
                    className="h-7 px-2 flex items-center gap-1 rounded-lg transition-all text-xs font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: selectedSessionIds.size > 0 ? 'rgba(255, 85, 85, 0.15)' : 'transparent',
                      color: 'var(--color-error)',
                      border: selectedSessionIds.size > 0 ? '1px solid var(--color-error)' : '1px solid transparent'
                    }}
                    title="Delete selected sessions"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    <span>{selectedSessionIds.size}</span>
                  </button>
                </>
              )}

              <button
                onClick={onClose}
                className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-[var(--color-hover)]"
                style={{ color: 'var(--text-muted)' }}
                title="Close"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <svg 
              viewBox="0 0 24 24" 
              fill="currentColor" 
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{ color: 'var(--text-muted)' }}
            >
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search sessions... (Ctrl+K)"
              className="input-field pl-8 w-full text-xs"
            />
          </div>
        </div>

        {/* Session List */}
        <div className="flex-1 overflow-auto scrollbar-thin p-2">
          {sortedSessions.length === 0 ? (
            <div 
              className="flex flex-col items-center justify-center h-32 text-center"
              style={{ color: 'var(--text-muted)' }}
            >
              <span className="text-2xl mb-2 opacity-50">📦</span>
              <span className="text-xs">No sessions found</span>
            </div>
          ) : (
            <div className="space-y-1.5">
              {sortedSessions.map(session => (
                <SessionCard
                  key={session.session_id}
                  session={session}
                  isActive={session.session_id === currentSessionId}
                  isSelectMode={isSelectMode}
                  isSelected={selectedSessionIds.has(session.session_id)}
                  onToggleSelect={(e) => handleToggleSelect(session.session_id, e)}
                  onClick={() => handleSessionClick(session.session_id)}
                  onDeleteClick={() => handleDelete(session.session_id)}
                  isDeleting={deletingSessionId === session.session_id}
                />
              ))}
            </div>
          )}
        </div>
      </aside>

      {deletingSessionId && (
        <div
          className="fixed inset-0 z-[102] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <div
              className="rounded-full flex items-center justify-center animate-spin"
              style={{
                width: '64px',
                height: '64px',
                border: '3px solid transparent',
                borderTopColor: 'var(--color-accent)'
              }}
            />
            <div>
              <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {deleteMessage}
              </p>
              {!deleteMessage.includes('deleted') && !deleteMessage.includes('Failed') && (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Please wait...
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
})

const SessionCard = memo(function SessionCard({ 
  session, 
  isActive, 
  isSelectMode, 
  isSelected, 
  onToggleSelect, 
  onClick, 
  onDeleteClick, 
  isDeleting = false 
}) {
  const levelSummary = session.levelSummary || { ERROR: 0, WARN: 0, INFO: 0, DEBUG: 0, OTHER: 0 }
  const hasErrors = levelSummary.ERROR > 0
  const hasWarns = levelSummary.WARN > 0

  return (
    <div
      className={`
        w-full text-left p-2.5 rounded-lg transition-all duration-150
        border flex items-start gap-2
        ${isActive ? 'ring-1 ring-[var(--color-accent)]' : 'hover:bg-[var(--color-hover)]'}
      `}
      style={{
        backgroundColor: isActive ? 'var(--color-active)' : 'var(--bg-tertiary)',
        borderColor: isActive ? 'var(--color-accent)' : 'transparent'
      }}
    >
      {/* Checkbox for Select Mode */}
      {isSelectMode && (
        <div className="pt-0.5 flex-shrink-0">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 rounded cursor-pointer accent-[var(--color-accent)]"
          />
        </div>
      )}

      <div className="flex-1 min-w-0">
        {/* Session ID & Actions */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <button
            onClick={onClick}
            className="font-mono text-xs font-medium truncate text-left hover:opacity-90 active:scale-[0.98] transition-transform"
            style={{ color: 'var(--text-primary)' }}
            title={session.session_id}
          >
            {truncate(session.session_id, 20)}
          </button>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
              style={{
                backgroundColor: hasErrors ? 'var(--color-error)' : hasWarns ? 'var(--color-warn)' : 'var(--bg-primary)',
                color: hasErrors || hasWarns ? 'var(--bg-primary)' : 'var(--text-muted)'
              }}
            >
              {session.logCount}
            </span>

            {/* Single Delete Icon Button */}
            {!isSelectMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDeleteClick()
                }}
                className="w-6 h-6 rounded-md flex items-center justify-center transition-colors hover:bg-[var(--color-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete session"
                aria-label={`Delete session ${session.session_id}`}
                style={{ color: 'var(--color-error)' }}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        <button
          onClick={onClick}
          className="w-full text-left"
          aria-label={`Open session ${session.session_id}`}
        >
          {/* Time */}
          <div
            className="text-[10px] mb-1.5 flex items-center gap-1"
            style={{ color: 'var(--text-muted)' }}
            title={formatDateTime(session.lastActivity)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 opacity-60">
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
            </svg>
            {formatRelativeTime(session.lastActivity)}
          </div>

          {/* Level Indicators */}
          <div className="flex gap-1 flex-wrap">
            {levelSummary.ERROR > 0 && <LevelBadge level="ERROR" count={levelSummary.ERROR} />}
            {levelSummary.WARN > 0 && <LevelBadge level="WARN" count={levelSummary.WARN} />}
            {levelSummary.INFO > 0 && <LevelBadge level="INFO" count={levelSummary.INFO} />}
            {levelSummary.DEBUG > 0 && <LevelBadge level="DEBUG" count={levelSummary.DEBUG} />}
          </div>
        </button>
      </div>
    </div>
  )
})

const levelBadgeStyles = {
  ERROR: { bg: 'rgba(255,85,85,0.2)', color: 'var(--color-error)' },
  WARN: { bg: 'rgba(255,184,108,0.2)', color: 'var(--color-warn)' },
  INFO: { bg: 'rgba(139,233,253,0.2)', color: 'var(--color-info)' },
  DEBUG: { bg: 'rgba(98,114,164,0.2)', color: 'var(--color-debug)' }
}

const LevelBadge = memo(function LevelBadge({ level, count }) {
  const style = levelBadgeStyles[level] || levelBadgeStyles.DEBUG
  return (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded font-semibold tabular-nums"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {level.slice(0, 3)} {count}
    </span>
  )
})
